import { Handler, replaceInChain, addToEndOfChain } from "@insite/client-framework/HandlerCreator";
import displayProducts, {
    ParseQueryParameter,
    SetIsFiltered,
    PopulateApiParameter,
    chain,
    RemoveNotAvailableAttributeValues,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import {
    GetProductCollectionApiV2Parameter,
    ProductExpandTokens,
} from "@insite/client-framework/Services/ProductServiceV2";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import {
    DisplayProductsResult,
    LoadRealTimePrices,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import qs, { ParsedQs } from "qs";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { pushDataToDataLayer } from "@NA/Overrides/Common/Utilities/CustomTracking";

import loadRealTimePricing from "@NA/Overrides/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductFilters } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";

interface Parameter {
    queryString: string;
    path: string;
    isSearch: boolean;
}
interface Props {
    apiParameter: GetProductCollectionApiV2Parameter;
    idByPath?: SafeDictionary<string>;
    result: DisplayProductsResult;
    realTimePricing?: RealTimePricingModel;
    pricingLoaded?: true;
    expandTokens?: ProductExpandTokens;
    tracked?: boolean;
}
type HandlerType = Handler<Parameter, Props>;

export const ParseQueryParameterCustom: HandlerType = props => {
    const { queryString } = props.parameter;
    const queryStringWithoutQuestionMark = queryString.startsWith("?") ? queryString.substr(1) : queryString;
    const parsedQuery = qs.parse(queryStringWithoutQuestionMark);
    const splitCommaSeparated = (value?: string | ParsedQs | (string | ParsedQs)[] | undefined) =>
        typeof value === "string" ? value.split(",") : undefined;
    const splitSpaceSeparated = (value?: string | ParsedQs | (string | ParsedQs)[] | undefined) =>
        value && typeof value === "string" ? value.split(" ") : undefined;
    const {
        query,
        page,
        sort,
        includeSuggestions,
        stockedItemsOnly,
        previouslyPurchasedProducts,
        categoryId,
        brandIds,
        productLineIds,
        priceFilters,
        attributes,
        searchWithinQueries,
    } = parsedQuery;

    let pageSize = parsedQuery.pageSize;

    if (!pageSize) {
        pageSize = getCookie("ProductList-PageSize");
    }

    const { catalogPage } = props.result;

    props.result.productFilters = {
        query,
        page: page ? parseInt(page as string, 10) : undefined,
        pageSize: pageSize ? parseInt(pageSize as string, 10) : undefined,
        sort: sort ? sort : props.parameter.isSearch && query ? "1" : undefined, //: sort ? sort : "1", // ADINA-46533 (Ankappa): This change is causing tilde routes to fail. Commenting out code for now, best match search fix will need to be revisited.
        includeSuggestions: includeSuggestions !== undefined ? includeSuggestions === "true" : undefined,
        stockedItemsOnly: stockedItemsOnly !== undefined ? stockedItemsOnly === "true" : undefined,
        previouslyPurchasedProducts:
            previouslyPurchasedProducts !== undefined ? previouslyPurchasedProducts === "true" : undefined,
        pageCategoryId: catalogPage?.categoryId,
        pageBrandId: catalogPage?.brandId || undefined,
        pageProductLineId: catalogPage?.productLineId || undefined,
        categoryId,
        brandIds: splitCommaSeparated(brandIds),
        productLineIds: splitCommaSeparated(productLineIds),
        priceFilters: splitCommaSeparated(priceFilters),
        attributes: splitCommaSeparated(attributes),
        searchWithinQueries: splitSpaceSeparated(searchWithinQueries),
    } as ProductFilters;
};

export const LoadRealTimePricesCustom: HandlerType = async props => {
    const {
        result: { productInfosByProductId },
    } = props;
    const productIds = Object.keys(productInfosByProductId);
    if (productIds.length === 0) {
        return;
    }

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "Pages/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });
            props.pricingLoaded = true;
            props.realTimePricing = realTimePricing;
            props.tracked = false;
            SendTrackingCustom(props);
        },
        onError: () => {
            props.dispatch({
                type: "Pages/ProductList/FailedLoadRealTimePricing",
            });
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    if (IS_SERVER_SIDE && getIsWebCrawler()) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};
export const SetIsFilteredCustom: HandlerType = ({ result, result: { productFilters } }) => {
    if (
        productFilters.searchWithinQueries?.length ||
        productFilters.brandIds?.length ||
        productFilters.productLineIds?.length ||
        productFilters.priceFilters?.length ||
        productFilters.attributes?.length ||
        productFilters.categoryId
    ) {
        result.isFiltered = true;
    }
};

export const PopulateApiParameterCustom: HandlerType = props => {
    const filters = props.result.productFilters;
    const { queryString } = props.parameter;
    const parsedQuery = qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString);
    const {
        query,
        brandIds,
        productLineIds,
        priceFilters,
        attributes,
        searchWithinQueries,
        pageCategoryId,
        pageBrandId,
        pageProductLineId,
        ...apiParameter
    } = filters;
    props.apiParameter = {
        ...apiParameter,
        search: query,
        categoryId: filters.categoryId || filters.pageCategoryId,
        searchWithin: searchWithinQueries?.join(" "),
        includeSuggestions: filters.includeSuggestions !== false,
        brandIds: filters.pageBrandId ? [filters.pageBrandId] : brandIds,
        productLineIds: filters.pageProductLineId ? [filters.pageProductLineId] : productLineIds,
        priceFilters,
        attributeValueIds: attributes,
        expand: props.expandTokens || [],
        applyPersonalization: true,
        includeAttributes: ["includeOnProduct"],
        additionalQueryStringParameters: {
            boostIds: parsedQuery.boostids,
            productNumbers: parsedQuery.erpNumbers,
            searchcriteria: parsedQuery.searchcriteria,
        },
    };
};

export const SendTrackingCustom: HandlerType = props => {
    const { isSearch } = props.parameter;
    const { result, realTimePricing, tracked } = props;
    const { products, productInfosByProductId } = result;
    const session = props.getState().context.session;

    if (products && ((!tracked && session.isAuthenticated && realTimePricing) || !session.isAuthenticated)) {
        if (isSearch) {
            pushDataToDataLayer(products, "Search Results", session, realTimePricing);
        } else {
            pushDataToDataLayer(products, "Product List Page", session, realTimePricing);
        }
        props.tracked = true;
    }
};

export const RemoveNotAvailableAttributeValuesCustom: HandlerType = props => {
    const productsDataView = getProductsDataView(props.getState(), props.apiParameter);
    if (!productsDataView.value) {
        return;
    }

    const possibleAttributeValueIds = new Set();
    productsDataView?.attributeTypeFacets?.forEach(a =>
        a.attributeValueFacets?.forEach(v => possibleAttributeValueIds.add(v.attributeValueId)),
    );
    const removedAttributeValueIds = props.result.productFilters.attributes?.filter(
        o => !possibleAttributeValueIds.has(o),
    );
    if (
        removedAttributeValueIds?.length &&
        !props.result.productFilters.stockedItemsOnly &&
        !props.result.productFilters.previouslyPurchasedProducts
    ) {
        const attributeValueIds = props.result.productFilters.attributes?.filter(o =>
            possibleAttributeValueIds.has(o),
        );
        props.dispatch(removeProductFilters({ attributeValueIds: removedAttributeValueIds }));
        const parsedQuery = parseQueryString<any>(props.parameter.queryString);
        parsedQuery.attributes = attributeValueIds?.length ? attributeValueIds.join(",") : undefined;
        const parameter = { ...props.parameter, queryString: qs.stringify(parsedQuery) };
        props.dispatch(displayProducts(parameter));
        return false;
    }
};

replaceInChain(chain, ParseQueryParameter, ParseQueryParameterCustom);
replaceInChain(chain, SetIsFiltered, SetIsFilteredCustom);
replaceInChain(chain, PopulateApiParameter, PopulateApiParameterCustom);
replaceInChain(chain, LoadRealTimePrices, LoadRealTimePricesCustom);
replaceInChain(chain, RemoveNotAvailableAttributeValues, RemoveNotAvailableAttributeValuesCustom);
addToEndOfChain(chain, SendTrackingCustom);
